import { useEffect, useState } from "react";
import type { Option, Grouped } from "../types";
import { useDebouncedState } from "@sprout/hooks/use_debounced_state";
import { PublicBaseSelectProps } from "react-select/base";

type LoaderFunction = (search: string, page: number) => Promise<Option[]>;

type AsyncReactSelectProps = Pick<
  PublicBaseSelectProps<Option, boolean, Grouped<Option>>,
  "isLoading" | "onMenuScrollToBottom" | "onInputChange" | "options"
>;

export const useAsyncReactSelectProps = (loadOptions: LoaderFunction): AsyncReactSelectProps => {
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [search, setSearch] = useDebouncedState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<Option[]>([]);
  const [page, setPage] = useState<number>(1);

  useEffect(() => {
    setPage(1);
    setIsLoading(true);

    loadOptions(search, 1)
      .then((newOptions) => setOptions(newOptions))
      .finally(() => setIsLoading(false));
  }, [search]);

  const loadNextPage = async () => {
    if (!hasMore) {
      return;
    }

    setIsLoading(true);
    setPage(page + 1);

    try {
      const newOptions = await loadOptions(search, page + 1);

      if (!newOptions || newOptions.length === 0) {
        setHasMore(false);
        return;
      }

      setOptions([...options, ...newOptions]);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    onInputChange: setSearch,
    onMenuScrollToBottom: loadNextPage,
    options,
  };
};
